/**
 * Minimal client-side wishlist (localStorage-backed, no account/app required).
 * PDP/PLP save-toggle buttons call EYDWishlist.toggle(productId); this file
 * only owns storage + keeping header count bubbles in sync across tabs.
 */
(() => {
  const STORAGE_KEY = 'eyd:wishlist';

  function readIds() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const ids = raw ? JSON.parse(raw) : [];
      return Array.isArray(ids) ? ids : [];
    } catch (error) {
      return [];
    }
  }

  function writeIds(ids) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
    updateCountBubbles(ids.length);
    document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { ids } }));
  }

  function updateCountBubbles(count) {
    document.querySelectorAll('[data-wishlist-count-bubble]').forEach((bubble) => {
      bubble.hidden = count < 1;
      const countEl = bubble.querySelector('[data-wishlist-count]');
      if (countEl) countEl.textContent = count < 100 ? count : '';
    });
  }

  window.EYDWishlist = {
    getIds: readIds,
    has(productId) {
      return readIds().includes(String(productId));
    },
    toggle(productId) {
      const id = String(productId);
      const ids = readIds();
      const index = ids.indexOf(id);
      if (index > -1) {
        ids.splice(index, 1);
      } else {
        ids.push(id);
      }
      writeIds(ids);
      return ids.includes(id);
    },
  };

  window.addEventListener('storage', (event) => {
    if (event.key === STORAGE_KEY) updateCountBubbles(readIds().length);
  });

  function syncToggleButton(button) {
    const productId = button.dataset.productId;
    const saved = window.EYDWishlist.has(productId);
    button.classList.toggle('is-active', saved);
    button.setAttribute('aria-pressed', saved);
    const label = button.querySelector('[data-wishlist-toggle-label]');
    if (label && button.dataset.labelSaved && button.dataset.labelDefault) {
      label.textContent = saved ? button.dataset.labelSaved : button.dataset.labelDefault;
    }
  }

  function syncAllToggleButtons() {
    document.querySelectorAll('[data-wishlist-toggle]').forEach(syncToggleButton);
  }

  function bindToggleButtons() {
    document.querySelectorAll('[data-wishlist-toggle]').forEach((button) => {
      if (button.dataset.wishlistBound) return;
      button.dataset.wishlistBound = 'true';

      const label = button.querySelector('[data-wishlist-toggle-label]');
      if (label && !button.dataset.labelDefault) {
        button.dataset.labelDefault = label.textContent.trim();
        button.dataset.labelSaved = 'Remove from Wishlist';
      }
      syncToggleButton(button);
      button.addEventListener('click', () => {
        window.EYDWishlist.toggle(button.dataset.productId);
      });
    });
  }

  document.addEventListener('DOMContentLoaded', () => {
    updateCountBubbles(readIds().length);
    bindToggleButtons();

    // The cart drawer's contents get replaced wholesale (innerHTML swap)
    // on every add-to-cart / quantity / remove change — see
    // CartDrawerItems.renderContents in cart-drawer.js — so any wishlist
    // toggle button rendered inside it (snippets/cart-drawer.liquid)
    // needs re-binding after each swap, not just once here. First tried
    // subscribing to PUB_SUB_EVENTS.cartUpdate (pubsub.js/constants.js
    // are both loaded sitewide ahead of this file, so `subscribe` is
    // available as a plain global) — but that event is published
    // *before* renderContents actually swaps the DOM (confirmed live:
    // the freshly-inserted button had no data-wishlist-bound attribute
    // immediately after an Add to Cart click), not after, so a rebind
    // triggered from it always ran one swap too early. A MutationObserver
    // doesn't depend on Dawn's internal call order — it just reacts to
    // the DOM actually changing, whichever code path did it.
    const cartDrawer = document.getElementById('CartDrawer');
    if (cartDrawer) {
      new MutationObserver(bindToggleButtons).observe(cartDrawer, { childList: true, subtree: true });
    }
  });
  document.addEventListener('wishlist:change', syncAllToggleButtons);
})();
